// This script uses data-* selectors as JavaScript hooks so behavior stays decoupled
// from styling classes and avoids the uniqueness constraints and reuse limits of ids.
// Default duration for expanding and collapsing individual drawer submenu panels.
const PANEL_ANIMATION_DURATION_MS = 400;
// Shared easing curve for submenu panel animations inside drawers.
const PANEL_ANIMATION_EASING = 'ease';
// Easing curve used for the taller primary drawer open/close motion.
const DRAWER_ANIMATION_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
// Key presses that should open a desktop menu and move focus into it.
const MENU_OPEN_KEYS = new Set(['Enter', ' ', 'ArrowDown']);
// Tracks which menu-set open classes have already been bound per root element.
const boundMenuSetRoots = new WeakMap();
// Tracks which roots have already had desktop main-nav listeners attached.
const boundMainNavRoots = new WeakSet();

// Reads a CSS custom property duration and normalizes it to milliseconds.
function readDurationMs(element, variableName, fallbackMs) {
  const rawValue = window.getComputedStyle(element).getPropertyValue(variableName).trim();

  if (!rawValue) {
    return fallbackMs;
  }

  if (rawValue.endsWith('ms')) {
    const parsed = Number.parseFloat(rawValue);
    return Number.isFinite(parsed) ? parsed : fallbackMs;
  }

  if (rawValue.endsWith('s')) {
    const parsed = Number.parseFloat(rawValue);
    return Number.isFinite(parsed) ? parsed * 1000 : fallbackMs;
  }

  const parsed = Number.parseFloat(rawValue);
  return Number.isFinite(parsed) ? parsed : fallbackMs;
}

// Converts a NodeList-like collection into a clean array of HTMLElements.
function asElementArray(nodeList) {
  return Array.from(nodeList).filter((node) => node instanceof HTMLElement);
}

// Keeps aria-expanded in sync with a trigger's open or closed state.
function setExpandedState(control, expanded) {
  control.setAttribute('aria-expanded', expanded ? 'true' : 'false');
}

// Keeps aria-hidden in sync with whether a panel should be exposed.
function setHiddenState(element, hidden) {
  element.setAttribute('aria-hidden', hidden ? 'true' : 'false');
}

// Returns the tabbable controls inside a container in DOM order.
function getFocusableElements(container) {
  if (!(container instanceof HTMLElement)) {
    return [];
  }

  return Array.from(
    container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter((element) => element instanceof HTMLElement);
}

// Collects item/trigger/menu triples for a menu family under a root.
function getMenuEntries(root, itemSelector, triggerSelector, menuSelector) {
  return asElementArray(root.querySelectorAll(itemSelector))
    .map((item) => {
      const trigger = item.querySelector(triggerSelector);
      const menu = item.querySelector(menuSelector);

      if (!(trigger instanceof HTMLElement) || !(menu instanceof HTMLElement)) {
        return null;
      }

      return { item, trigger, menu };
    })
    .filter(Boolean);
}

// Returns true when a keydown event should open a desktop menu.
function isMenuOpenKey(event) {
  return MENU_OPEN_KEYS.has(event.key);
}

// Resolves the activated anchor or button from an event target.
function getActivatedControl(target) {
  if (!(target instanceof Element)) {
    return null;
  }

  const control = target.closest('a, button');
  return control instanceof HTMLElement ? control : null;
}

// Chooses the first focus target when a desktop menu opens.
function getPreferredDesktopMenuFocusTarget(menu) {
  if (!(menu instanceof HTMLElement)) {
    return null;
  }

  const submenuLink = menu.querySelector('.listgroup--main-nav-submenu .list-item--main-nav-submenu');
  if (submenuLink instanceof HTMLElement) {
    return submenuLink;
  }

  return getFocusableElements(menu)[0] ?? null;
}

// Finds the last meaningful keyboard target inside a desktop menu.
function getLastDesktopMenuListTarget(menu) {
  if (!(menu instanceof HTMLElement)) {
    return null;
  }

  const submenuLinks = Array.from(
    menu.querySelectorAll('.listgroup--main-nav-submenu .list-item--main-nav-submenu')
  ).filter((element) => element instanceof HTMLElement);

  return submenuLinks.at(-1) ?? getFocusableElements(menu).at(-1) ?? null;
}

// Finds the next top-level nav trigger so Tab can advance across menus cleanly.
function getNextTopLevelNavTarget(item) {
  if (!(item instanceof HTMLElement)) {
    return null;
  }

  let sibling = item.nextElementSibling;

  while (sibling instanceof HTMLElement) {
    const target = sibling.querySelector('.header__nav-link, button, a[href]');
    if (target instanceof HTMLElement) {
      return target;
    }

    sibling = sibling.nextElementSibling;
  }

  return null;
}

// Handles forward-Tab from the last interactive item in a menu to the next top-level trigger.
function moveFocusToNextTopLevelTarget(event, menu, item, beforeFocus) {
  if (event.key !== 'Tab' || event.shiftKey) {
    return false;
  }

  const activeElement = document.activeElement;
  const lastListTarget = getLastDesktopMenuListTarget(menu);

  if (!(activeElement instanceof HTMLElement) || activeElement !== lastListTarget) {
    return false;
  }

  const nextTarget = getNextTopLevelNavTarget(item);
  if (!(nextTarget instanceof HTMLElement)) {
    return false;
  }

  event.preventDefault();
  beforeFocus?.();
  nextTarget.focus();
  return true;
}

// Toggles the promo corner modifier based on whether the link column is taller than the promo block.
function updatePromoMenuCornerState(menu) {
  if (!(menu instanceof HTMLElement) || !menu.classList.contains('header__nav-listgroup--promo')) {
    return;
  }

  const list = menu.querySelector('.list-group:last-child');
  const promo = menu.querySelector('.header__nav-promo');

  if (!(list instanceof HTMLElement) || !(promo instanceof HTMLElement)) {
    menu.classList.remove('header__nav-listgroup--promo-flush-end');
    return;
  }

  const listHeight = list.getBoundingClientRect().height;
  const promoHeight = promo.getBoundingClientRect().height;

  menu.classList.toggle('header__nav-listgroup--promo-flush-end', listHeight > promoHeight + 1);
}

// Measures a menu's scroll height and stores it in CSS for animation/layout use.
function updateMenuMaxHeight(menu) {
  if (!(menu instanceof HTMLElement)) {
    return;
  }

  const measuredHeight = menu.scrollHeight;

  if (measuredHeight > 0) {
    menu.style.setProperty('--header-nav-menu-max-height', `${measuredHeight}px`);
  }
}

// Removes the temporary suppression flag that forces a nav item closed.
function clearMainNavSuppression(item) {
  if (item instanceof HTMLElement) {
    item.removeAttribute('data-nav-menu-suppressed');
  }
}

// Adds a temporary suppression flag so hover/focus alone will not reopen an item.
function suppressMainNavItem(item) {
  if (item instanceof HTMLElement) {
    item.setAttribute('data-nav-menu-suppressed', '');
  }
}

// Determines whether a main-nav item should currently be treated as open.
function isMainNavItemOpen(item) {
  if (!(item instanceof HTMLElement) || item.hasAttribute('data-nav-menu-suppressed')) {
    return false;
  }

  return item.matches(':hover') || item.matches(':focus-within');
}

// Synchronizes trigger, panel, and promo-specific classes for one main-nav item.
function syncMainNavItemState(item) {
  const trigger = item.querySelector('[data-header-menu-trigger]');
  const menu = item.querySelector('.header__nav-listgroup');

  if (!(trigger instanceof HTMLElement) || !(menu instanceof HTMLElement)) {
    return;
  }

  const open = isMainNavItemOpen(item);

  setExpandedState(trigger, open);
  setHiddenState(menu, !open);

  if (open) {
    updatePromoMenuCornerState(menu);
  } else {
    menu.classList.remove('header__nav-listgroup--promo-flush-end');
  }
}

// Blurs the currently focused element if it lives inside the supplied item.
function blurFocusedWithin(item) {
  if (!(item instanceof HTMLElement)) {
    return;
  }

  const activeElement = document.activeElement;

  if (activeElement instanceof HTMLElement && item.contains(activeElement)) {
    activeElement.blur();
  }
}

// Binds the desktop-only behavior for the primary header navigation.
function initDesktopMainNav(root = document) {
  // The element used to dedupe event binding for this root.
  const bindRoot = root instanceof Document ? root.documentElement : root;

  if (!(bindRoot instanceof HTMLElement) || boundMainNavRoots.has(bindRoot)) {
    return;
  }

  boundMainNavRoots.add(bindRoot);

  // Media query that enables the large-screen hover/focus nav pattern.
  const desktopQuery = window.matchMedia('(min-width: 64rem)');
  // The top-level nav list that owns the primary desktop menu items.
  const navList = root.querySelector('.header__nav-list');

  if (!(navList instanceof HTMLElement)) {
    return;
  }

  // Cached item/trigger/menu triples for every main-nav item with a submenu.
  const items = getMenuEntries(
    navList,
    '.header__nav-item:has([data-header-menu-trigger])',
    '[data-header-menu-trigger]',
    '.header__nav-listgroup'
  );

  // Resolves any descendant node back to its cached main-nav entry.
  function getItemEntryFromNode(node) {
    if (!(node instanceof Element)) {
      return null;
    }

    const item = node.closest('.header__nav-item');

    if (!(item instanceof HTMLElement)) {
      return null;
    }

    return items.find((entry) => entry.item === item) ?? null;
  }

  items.forEach((entry) => {
    setExpandedState(entry.trigger, false);
    setHiddenState(entry.menu, true);
    updateMenuMaxHeight(entry.menu);

    entry.item.addEventListener('mouseenter', () => {
      if (!desktopQuery.matches) {
        return;
      }

      clearMainNavSuppression(entry.item);
      syncMainNavItemState(entry.item);
    });

    entry.item.addEventListener('mouseleave', () => {
      if (!desktopQuery.matches) {
        return;
      }

      window.requestAnimationFrame(() => {
        syncMainNavItemState(entry.item);
      });
    });

    entry.trigger.addEventListener('click', (event) => {
      if (!desktopQuery.matches) {
        return;
      }

      if (entry.trigger.getAttribute('href') === '#') {
        event.preventDefault();
      }

      clearMainNavSuppression(entry.item);

      window.requestAnimationFrame(() => {
        syncMainNavItemState(entry.item);
      });
    });

    entry.trigger.addEventListener('keydown', (event) => {
      if (!desktopQuery.matches) {
        return;
      }

      if (!isMenuOpenKey(event)) {
        return;
      }

      event.preventDefault();
      clearMainNavSuppression(entry.item);
      syncMainNavItemState(entry.item);

      const focusTarget = getPreferredDesktopMenuFocusTarget(entry.menu);
      focusTarget?.focus();
    });

    entry.menu.addEventListener('click', (event) => {
      if (!desktopQuery.matches) {
        return;
      }

      const activatedControl = getActivatedControl(event.target);
      if (!(activatedControl instanceof HTMLElement)) {
        return;
      }

      suppressMainNavItem(entry.item);
      blurFocusedWithin(entry.item);
      window.requestAnimationFrame(() => {
        syncMainNavItemState(entry.item);
      });
    });

    entry.menu.addEventListener('keydown', (event) => {
      if (!desktopQuery.matches) {
        return;
      }

      moveFocusToNextTopLevelTarget(event, entry.menu, entry.item, () => {
        blurFocusedWithin(entry.item);
      });
    });
  });

  navList.addEventListener('focusin', (event) => {
    if (!desktopQuery.matches) {
      return;
    }

    const entry = getItemEntryFromNode(event.target);

    if (entry) {
      syncMainNavItemState(entry.item);
    }
  });

  navList.addEventListener('focusout', (event) => {
    if (!desktopQuery.matches) {
      return;
    }

    const entry = getItemEntryFromNode(event.target);

    if (!entry) {
      return;
    }

    window.requestAnimationFrame(() => {
      if (!entry.item.contains(document.activeElement)) {
        syncMainNavItemState(entry.item);
      }
    });
  });

  document.addEventListener('click', (event) => {
    if (!desktopQuery.matches || !(event.target instanceof Node)) {
      return;
    }

    items.forEach((entry) => {
      if (!entry.item.contains(event.target)) {
        suppressMainNavItem(entry.item);
        blurFocusedWithin(entry.item);
        syncMainNavItemState(entry.item);
      }
    });
  });

  document.addEventListener('keydown', (event) => {
    if (!desktopQuery.matches || event.key !== 'Escape') {
      return;
    }

    const activeEntry = items.find((entry) => entry.item.contains(document.activeElement));

    if (!activeEntry) {
      return;
    }

    suppressMainNavItem(activeEntry.item);
    blurFocusedWithin(activeEntry.item);
    activeEntry.trigger.focus();
    syncMainNavItemState(activeEntry.item);
  });

  window.addEventListener('resize', () => {
    if (!desktopQuery.matches) {
      return;
    }

    items.forEach((entry) => {
      updateMenuMaxHeight(entry.menu);

      if (isMainNavItemOpen(entry.item)) {
        updatePromoMenuCornerState(entry.menu);
      }
    });
  });
}

// Binds the shared desktop menu behavior used by action menus and subnav menus.
function initDesktopMenuSet({ root = document, mediaQuery, itemSelector, triggerSelector, menuSelector, openClass }) {
  // The element used to dedupe this specific menu set per root.
  const bindRoot = root instanceof Document ? root.documentElement : root;

  if (!(bindRoot instanceof HTMLElement)) {
    return;
  }

  // Per-root registry of open-class bindings that have already been initialized.
  let boundOpenClasses = boundMenuSetRoots.get(bindRoot);

  if (!boundOpenClasses) {
    boundOpenClasses = new Set();
    boundMenuSetRoots.set(bindRoot, boundOpenClasses);
  }

  if (boundOpenClasses.has(openClass)) {
    return;
  }

  boundOpenClasses.add(openClass);

  // Media query that gates this menu set to its desktop breakpoint.
  const desktopQuery = window.matchMedia(mediaQuery);
  // Cached item/trigger/menu triples for this menu family.
  const items = getMenuEntries(root, itemSelector, triggerSelector, menuSelector);

  // Opens or closes a single item while syncing classes and ARIA state.
  function setOpen(entry, open) {
    entry.item.classList.toggle(openClass, open);
    setExpandedState(entry.trigger, open);
    setHiddenState(entry.menu, !open);
  }

  // Closes every menu in the set except the optional active item.
  function closeAll(except = null) {
    items.forEach((entry) => {
      if (entry !== except) {
        setOpen(entry, false);

        if (document.activeElement === entry.trigger && !entry.trigger.matches(':focus-visible')) {
          entry.trigger.blur();
        }
      }
    });
  }

  items.forEach((entry) => {
    setOpen(entry, false);

    entry.trigger.addEventListener('click', (event) => {
      if (!desktopQuery.matches) {
        return;
      }

      event.preventDefault();

      const isOpen = entry.item.classList.contains(openClass);
      closeAll(entry);
      setOpen(entry, !isOpen);
    });

    entry.trigger.addEventListener('keydown', (event) => {
      if (!desktopQuery.matches) {
        return;
      }

      if (!isMenuOpenKey(event)) {
        return;
      }

      event.preventDefault();
      closeAll(entry);
      setOpen(entry, true);

      const focusTarget = getPreferredDesktopMenuFocusTarget(entry.menu);
      focusTarget?.focus();
    });

    entry.item.addEventListener('mouseleave', () => {
      if (!desktopQuery.matches) {
        return;
      }

      setOpen(entry, false);
    });

    entry.menu.addEventListener('click', (event) => {
      if (!desktopQuery.matches) {
        return;
      }

      const activatedControl = getActivatedControl(event.target);
      if (!(activatedControl instanceof HTMLElement)) {
        return;
      }

      activatedControl.blur();

      if (document.activeElement === entry.trigger) {
        entry.trigger.blur();
      }

      setOpen(entry, false);
    });

    entry.menu.addEventListener('keydown', (event) => {
      if (!desktopQuery.matches) {
        return;
      }

      moveFocusToNextTopLevelTarget(event, entry.menu, entry.item, () => {
        closeAll();
        entry.trigger.blur();
      });
    });
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Node)) {
      return;
    }

    items.forEach((entry) => {
      if (!entry.item.contains(event.target)) {
        setOpen(entry, false);
      }
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      const activeEntry = items.find((entry) => entry.item.contains(document.activeElement));

      if (!activeEntry) {
        return;
      }

      closeAll();
      activeEntry.trigger.focus();
    }
  });
}

// Initializes all desktop header menu systems for the current root.
function initDesktopMenus(root = document) {
  initDesktopMainNav(root);

  initDesktopMenuSet({
    root,
    mediaQuery: '(min-width: 48rem)',
    itemSelector: '.header__action-item',
    triggerSelector: '[data-header-action-trigger]',
    menuSelector: '.header__action-menu',
    openClass: 'header__action-item--open',
  });

  initDesktopMenuSet({
    root,
    mediaQuery: '(min-width: 90rem)',
    itemSelector: '.header-subnav__item',
    triggerSelector: '[data-header-subnav-trigger]',
    menuSelector: '.header-subnav__menu',
    openClass: 'header-subnav__item--open',
  });
}

// Binds responsive drawer behavior shared by the primary nav, actions drawer, and subnav drawer.
function initResponsiveDrawer({
  root = document,
  mediaQuery,
  toggleSelector,
  drawerSelector,
  onOpen,
  onClose,
  onHeightChange,
}) {
  // Media query that decides when this drawer version is active.
  const drawerQuery = window.matchMedia(mediaQuery);
  // Media query used to disable animation when the user prefers reduced motion.
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Button that toggles this drawer open and closed.
  const toggle = root.querySelector(toggleSelector);
  // Drawer shell element that animates its own height.
  const drawer = root.querySelector(drawerSelector);
  // Inner content wrapper whose scrollHeight defines the drawer's open height.
  const drawerInner = drawer?.querySelector('.header-drawer__inner');
  // Remembers whether closing should return focus to the drawer toggle button.
  let returnFocusToToggle = false;
  // requestAnimationFrame id used while tracking animated height changes.
  let layoutFrameId = null;

  if (
    !(toggle instanceof HTMLButtonElement) ||
    !(drawer instanceof HTMLElement) ||
    !(drawerInner instanceof HTMLElement)
  ) {
    return;
  }

  if (drawer.dataset.headerDrawerBound === 'true') {
    return;
  }

  drawer.dataset.headerDrawerBound = 'true';

  // Returns all drawer groups that can expand nested submenus.
  function getGroups() {
    return Array.from(drawer.querySelectorAll('[data-header-drawer-group]'));
  }

  // Measures the full height the drawer needs when fully open.
  function getDrawerHeight() {
    return drawerInner.scrollHeight;
  }

  // Convenience wrapper for the reduced-motion media query.
  function prefersReducedMotion() {
    return reducedMotionQuery.matches;
  }

  // Writes an explicit pixel height to the drawer shell.
  function setDrawerHeight(height) {
    drawer.style.height = `${Math.max(0, height)}px`;
  }

  // Reports the current drawer height to any external layout callback.
  function syncDrawerHeight() {
    onHeightChange?.(drawer.getBoundingClientRect().height);
  }

  // Cancels any active requestAnimationFrame height tracking loop.
  function stopLayoutTracking() {
    if (layoutFrameId !== null) {
      window.cancelAnimationFrame(layoutFrameId);
      layoutFrameId = null;
    }
  }

  // Checks whether any nested drawer group panel is still animating.
  function hasGroupAnimation() {
    return getGroups().some((group) => {
      const panel = group.querySelector('.header-drawer__submenu');
      return panel?.__headerDrawerAnimation;
    });
  }

  // Continuously reports drawer height while the shell or its groups are animating.
  function startLayoutTracking() {
    if (!onHeightChange) {
      return;
    }

    stopLayoutTracking();

    const tick = () => {
      syncDrawerHeight();

      if (drawer.__headerDrawerAnimation || hasGroupAnimation()) {
        layoutFrameId = window.requestAnimationFrame(tick);
        return;
      }

      layoutFrameId = null;
    };

    tick();
  }

  // Sets the drawer height without animating so breakpoint and reduced-motion transitions stay in sync.
  function setDrawerHeightImmediate(height) {
    const previousTransition = drawer.style.transition;
    drawer.style.transition = 'none';
    setDrawerHeight(height);
    drawer.getBoundingClientRect();
    drawer.style.transition = previousTransition;
    syncDrawerHeight();
  }

  // Cancels the current drawer shell animation and stops height tracking.
  function stopDrawerAnimations() {
    drawer.__headerDrawerAnimation?.cancel();
    drawer.__headerDrawerAnimation = null;
    stopLayoutTracking();
  }

  // Animates the drawer shell between its current height and its open or closed height.
  function animateDrawer(open) {
    stopDrawerAnimations();

    // CSS-configured duration for the drawer shell animation.
    const drawerDuration = readDurationMs(drawer, '--header-drawer-duration', 520);
    // Current rendered height at the moment the animation starts.
    const startHeight = drawer.getBoundingClientRect().height;
    // Height the drawer should end at after opening or closing.
    const endHeight = open ? getDrawerHeight() : 0;

    drawer.style.height = `${startHeight}px`;
    drawer.getBoundingClientRect();

    const animation = drawer.animate([{ height: `${startHeight}px` }, { height: `${endHeight}px` }], {
      duration: drawerDuration,
      easing: DRAWER_ANIMATION_EASING,
      fill: 'forwards',
    });

    drawer.__headerDrawerAnimation = animation;
    startLayoutTracking();

    animation.onfinish = () => {
      drawer.__headerDrawerAnimation = null;
      drawer.style.height = open ? 'auto' : '0px';
      syncDrawerHeight();
      stopLayoutTracking();

      if (!open) {
        drawer.classList.remove('header-drawer--closing');

        if (returnFocusToToggle) {
          toggle.focus();
          returnFocusToToggle = false;
        }
      }
    };

    animation.oncancel = () => {
      drawer.__headerDrawerAnimation = null;
      syncDrawerHeight();
      stopLayoutTracking();
    };
  }

  // Closes the drawer, either immediately or with animation.
  function closeDrawer({ immediate = false } = {}) {
    onClose?.();
    setExpandedState(toggle, false);
    setHiddenState(drawer, true);

    if (immediate || prefersReducedMotion()) {
      stopDrawerAnimations();
      drawer.classList.remove('header-drawer--open', 'header-drawer--closing');
      setDrawerHeightImmediate(0);

      if (returnFocusToToggle) {
        toggle.focus();
        returnFocusToToggle = false;
      }

      return;
    }

    drawer.classList.remove('header-drawer--open');
    drawer.classList.add('header-drawer--closing');
    animateDrawer(false);
  }

  // Opens the drawer, either immediately or with animation.
  function openDrawer({ immediate = false } = {}) {
    onOpen?.();
    setExpandedState(toggle, true);
    setHiddenState(drawer, false);
    drawer.classList.remove('header-drawer--closing');
    drawer.classList.add('header-drawer--open');

    if (immediate || prefersReducedMotion()) {
      stopDrawerAnimations();
      setDrawerHeightImmediate(getDrawerHeight());
      return;
    }

    animateDrawer(true);
  }

  // Expands or collapses a single nested drawer group.
  function setGroupOpen(group, open) {
    const groupToggle = group.querySelector('[data-header-drawer-group-toggle]');
    const panel = group.querySelector('.header-drawer__submenu');

    if (!(groupToggle instanceof HTMLButtonElement) || !(panel instanceof HTMLElement)) {
      return;
    }

    // Any in-flight panel animation must stop before recalculating heights.
    const existingAnimation = panel.__headerDrawerAnimation;
    if (existingAnimation) {
      existingAnimation.cancel();
    }

    // Current visible height before opening or closing the group.
    const startHeight = panel.getBoundingClientRect().height;

    if (open) {
      group.classList.add('header-drawer__group--open');
      setExpandedState(groupToggle, true);
      setHiddenState(panel, false);
    }

    // Target height of the submenu panel after state changes.
    const endHeight = open ? panel.scrollHeight : 0;

    if (prefersReducedMotion()) {
      if (!open) {
        group.classList.remove('header-drawer__group--open');
        setExpandedState(groupToggle, false);
      }

      setHiddenState(panel, !open);
      panel.style.height = open ? 'auto' : '0px';
      syncDrawerHeight();
      return;
    }

    panel.style.height = `${startHeight}px`;
    panel.getBoundingClientRect();

    const animation = panel.animate([{ height: `${startHeight}px` }, { height: `${endHeight}px` }], {
      duration: PANEL_ANIMATION_DURATION_MS,
      easing: PANEL_ANIMATION_EASING,
      fill: 'forwards',
    });

    panel.__headerDrawerAnimation = animation;
    startLayoutTracking();

    animation.onfinish = () => {
      panel.__headerDrawerAnimation = null;

      if (open) {
        setHiddenState(panel, false);
        panel.style.height = 'auto';
        syncDrawerHeight();
        stopLayoutTracking();
        return;
      }

      group.classList.remove('header-drawer__group--open');
      setExpandedState(groupToggle, false);
      setHiddenState(panel, true);
      panel.style.height = '0px';
      syncDrawerHeight();
      stopLayoutTracking();
    };

    animation.oncancel = () => {
      panel.__headerDrawerAnimation = null;
      syncDrawerHeight();
      stopLayoutTracking();
    };
  }

  // Applies a group's open state synchronously without animation.
  function syncGroupState(group, open) {
    const groupToggle = group.querySelector('[data-header-drawer-group-toggle]');
    const panel = group.querySelector('.header-drawer__submenu');

    if (!(groupToggle instanceof HTMLButtonElement) || !(panel instanceof HTMLElement)) {
      return;
    }

    group.classList.toggle('header-drawer__group--open', open);
    setExpandedState(groupToggle, open);
    setHiddenState(panel, !open);
    panel.style.height = open ? 'auto' : '0px';
  }

  // Forces a group's panel height to its open or closed value with transitions temporarily disabled.
  function setGroupHeightImmediate(group, open) {
    const panel = group.querySelector('.header-drawer__submenu');

    if (!(panel instanceof HTMLElement)) {
      return;
    }

    const previousTransition = panel.style.transition;
    panel.style.transition = 'none';
    panel.style.height = open ? `${panel.scrollHeight}px` : '0px';
    panel.getBoundingClientRect();
    panel.style.transition = previousTransition;
  }

  // Closes every group except the optional active one.
  function closeGroups(except = null) {
    getGroups().forEach((group) => {
      if (group !== except) {
        setGroupOpen(group, false);
      }
    });
  }

  // Ensures the drawer is closed when its breakpoint is no longer active.
  function syncDrawerState() {
    if (!drawerQuery.matches) {
      closeDrawer({ immediate: true });
    }
  }

  syncDrawerState();
  setHiddenState(drawer, !drawer.classList.contains('header-drawer--open'));
  syncDrawerHeight();

  // Binds click handlers and initial state for each drawer group, including dynamically rebuilt groups.
  function bindGroups() {
    getGroups().forEach((group) => {
      if (group.dataset.headerDrawerGroupBound === 'true') {
        return;
      }

      group.dataset.headerDrawerGroupBound = 'true';

      const groupToggle = group.querySelector('[data-header-drawer-group-toggle]');

      if (!(groupToggle instanceof HTMLButtonElement)) {
        return;
      }

      const startsOpen = group.classList.contains('header-drawer__group--open');
      syncGroupState(group, startsOpen);
      setGroupHeightImmediate(group, startsOpen);

      groupToggle.addEventListener('click', (event) => {
        event.preventDefault();

        if (!drawerQuery.matches) {
          return;
        }

        const isOpen = group.classList.contains('header-drawer__group--open');
        closeGroups(group);
        setGroupOpen(group, !isOpen);
      });
    });
  }

  bindGroups();

  // Exposes a rebinding hook for drawers whose inner group markup gets regenerated later.
  drawer.__bindHeaderDrawerGroups = bindGroups;

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Node)) {
      return;
    }

    const clickedToggle = event.target.closest(toggleSelector);
    if (clickedToggle) {
      event.preventDefault();

      if (!drawerQuery.matches) {
        return;
      }

      if (drawer.classList.contains('header-drawer--open')) {
        returnFocusToToggle = true;
        closeDrawer();
      } else {
        openDrawer();
      }
      return;
    }

    const clickedClose = event.target.closest('[data-header-drawer-close]');
    if (clickedClose && drawer.contains(clickedClose)) {
      event.preventDefault();

      if (!drawerQuery.matches) {
        return;
      }

      closeGroups();
      returnFocusToToggle = true;
      closeDrawer();
      return;
    }

    const clickedDrawerLink =
      event.target instanceof Element
        ? event.target.closest('.header-drawer__link, .header-drawer__submenu-item')
        : null;
    if (clickedDrawerLink && drawer.contains(clickedDrawerLink)) {
      if (!drawerQuery.matches) {
        return;
      }

      closeGroups();
      returnFocusToToggle = false;
      closeDrawer();
      return;
    }

    if (
      drawer.classList.contains('header-drawer--open') &&
      !drawer.contains(event.target) &&
      !toggle.contains(event.target)
    ) {
      closeGroups();
      returnFocusToToggle = false;
      closeDrawer();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeGroups();
      returnFocusToToggle = drawer.contains(document.activeElement);
      closeDrawer();
    }
  });

  drawerQuery.addEventListener('change', () => {
    if (!drawerQuery.matches) {
      closeGroups();
    }

    syncDrawerState();
  });
}

// Rebuilds the mobile subnav drawer from the desktop subnav structure.
function initResponsiveSubnavDrawer(root = document) {
  // Desktop subnav shell that provides the source content.
  const subnav = root.querySelector('.header-subnav');
  // Original desktop subnav list.
  const list = subnav?.querySelector('.header-subnav__list');
  // Drawer list container that receives the rebuilt mobile markup.
  const drawerList = root.querySelector('[data-header-subnav-drawer-list]');
  // Snapshot of source subnav items used to rebuild the drawer.
  const items = asElementArray(list?.children ?? []);

  if (!(list instanceof HTMLElement) || !(drawerList instanceof HTMLElement)) {
    return;
  }

  // Returns the primary clickable link for a desktop subnav item.
  function getItemLink(item) {
    return item.querySelector('.header-subnav__link');
  }

  // Clones desktop subnav links and menus into drawer-compatible markup.
  function rebuildDrawerItems() {
    drawerList.innerHTML = '';

    items.forEach((item) => {
      const sourceLink = getItemLink(item);

      if (!(sourceLink instanceof HTMLAnchorElement)) {
        return;
      }

      const li = document.createElement('li');
      const sourceMenu = item.querySelector('.header-subnav__menu');

      if (sourceMenu instanceof HTMLElement) {
        li.className = 'header-drawer__group';
        li.setAttribute('data-header-drawer-group', '');

        const button = document.createElement('button');
        button.className = 'header-drawer__item';
        button.type = 'button';
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-haspopup', 'true');

        const submenuId = `header-subnav-drawer-group-${drawerList.children.length + 1}`;
        button.setAttribute('aria-controls', submenuId);
        button.setAttribute('data-header-drawer-group-toggle', '');

        const label = document.createElement('span');
        label.className = 'header-drawer__item-label';
        label.textContent = sourceLink.textContent?.trim() ?? '';

        const chevron = document.createElement('span');
        chevron.className = 'header-drawer__chevron';
        chevron.setAttribute('aria-hidden', 'true');

        button.append(label, chevron);

        const submenu = document.createElement('ul');
        submenu.className = 'list-group header-drawer__submenu';
        submenu.id = submenuId;

        asElementArray(sourceMenu.querySelectorAll('.list-item')).forEach((sourceMenuLink) => {
          if (!(sourceMenuLink instanceof HTMLAnchorElement)) {
            return;
          }

          const submenuLi = document.createElement('li');
          const submenuLink = document.createElement('a');
          submenuLink.className = 'header-drawer__submenu-item';
          submenuLink.href = sourceMenuLink.getAttribute('href') ?? '#';
          submenuLink.textContent = sourceMenuLink.textContent?.trim() ?? '';
          submenuLi.append(submenuLink);
          submenu.append(submenuLi);
        });

        li.append(button, submenu);
        drawerList.append(li);
        return;
      }

      const link = document.createElement('a');
      const label = document.createElement('span');
      link.className = 'header-drawer__link';
      link.href = sourceLink.getAttribute('href') ?? '#';
      label.className = 'header-drawer__item-label';
      label.textContent = sourceLink.textContent?.trim() ?? '';
      link.append(label);
      li.append(link);
      drawerList.append(li);
    });

    const drawer = root.querySelector('[data-header-subnav-drawer]');
    drawer?.__bindHeaderDrawerGroups?.();
  }

  rebuildDrawerItems();
}

// Wires up the collapsible header search panel and its focus behavior.
function initHeaderSearch(header) {
  // Button that opens and closes the search panel.
  const searchToggle = header?.querySelector('[data-header-search-toggle]');
  // Search panel container that is shown and hidden.
  const searchPanel = header?.querySelector('[data-header-search-panel]');
  // Input field that should receive focus when search opens.
  const searchField = header?.querySelector('[data-header-search-field]');

  if (
    !(header instanceof HTMLElement) ||
    !(searchToggle instanceof HTMLButtonElement) ||
    !(searchPanel instanceof HTMLElement) ||
    !(searchField instanceof HTMLInputElement)
  ) {
    return;
  }

  if (header.dataset.headerSearchBound === 'true') {
    return;
  }

  header.dataset.headerSearchBound = 'true';

  // Opens or closes the search panel and optionally restores focus to the toggle.
  function setOpen(open, { returnFocus = false } = {}) {
    header.classList.toggle('header--search-open', open);
    setExpandedState(searchToggle, open);
    setHiddenState(searchPanel, !open);

    if (open) {
      window.requestAnimationFrame(() => {
        searchField.focus();
      });
      return;
    }

    if (returnFocus && header.contains(document.activeElement)) {
      searchToggle.focus();
      return;
    }

    if (document.activeElement === searchToggle && !searchToggle.matches(':focus-visible')) {
      searchToggle.blur();
    }
  }

  setOpen(false);

  searchToggle.addEventListener('click', (event) => {
    event.preventDefault();
    setOpen(!header.classList.contains('header--search-open'));
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Node)) {
      return;
    }

    const clickedInsideSearch = searchToggle.contains(event.target) || searchPanel.contains(event.target);

    if (!clickedInsideSearch) {
      setOpen(false);
    }
  });

  header.addEventListener('mouseleave', () => {
    if (!header.classList.contains('header--search-open')) {
      return;
    }

    const activeElement = document.activeElement;
    const engagedWithSearch =
      searchPanel.matches(':hover') ||
      searchPanel.matches(':focus-within') ||
      searchToggle.matches(':hover') ||
      searchToggle.matches(':focus-visible') ||
      activeElement === searchField;

    if (!engagedWithSearch) {
      setOpen(false);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && header.classList.contains('header--search-open')) {
      setOpen(false, { returnFocus: true });
    }
  });
}

// Initializes every header behavior for the supplied DOM root.
export function initHeader(root = document) {
  // The main header element that owns shared search and drawer state classes.
  const header = root.querySelector('.header');
  // Document used for writing layout CSS variables even when initialized from a subtree.
  const rootDocument = root instanceof Document ? root : root.ownerDocument;

  initDesktopMenus(root);
  initResponsiveSubnavDrawer(root);
  initHeaderSearch(header);

  initResponsiveDrawer({
    root,
    mediaQuery: '(width < 1440px)',
    toggleSelector: '[data-header-drawer-toggle]',
    drawerSelector: '[data-header-drawer]',
    onOpen: () => header?.classList.add('header--drawer-open'),
    onClose: () => header?.classList.remove('header--drawer-open'),
    onHeightChange: (height) =>
      rootDocument.documentElement.style.setProperty('--header-primary-drawer-offset', `${height}px`),
  });

  initResponsiveDrawer({
    root,
    mediaQuery: '(width < 768px)',
    toggleSelector: '[data-header-action-drawer-toggle]',
    drawerSelector: '[data-header-action-drawer]',
  });

  initResponsiveDrawer({
    root,
    mediaQuery: '(width < 1440px)',
    toggleSelector: '[data-header-subnav-drawer-toggle]',
    drawerSelector: '[data-header-subnav-drawer]',
  });
}

// Auto-initializes the header once the DOM is ready.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initHeader(), {
    once: true,
  });
} else {
  initHeader();
}
